import { StageDef } from '../automationMachineInterfaces'
import { TWITTER_AUTOMATION_STAGE_STATUSES } from './twitterAutomationStageStatuses'
import { mutedNotificationsSettingsUrl } from './twitterUrls'
import { XSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { TwitterRecommendationKey } from '../../utils/enums'
import * as enums from '../../utils/enums'
import {
  getCurrentValueForCheckbox,
  saveCurrentValueForCheckbox,
  toggleCheckbox,
} from '../checkboxDetection'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

// Settings property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'Muted' : 'Not Muted'))

const getIsFulfilledFn = (key) => getIsFulfilledFnFromKey(key, (value) => value)

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

// Constant associating each setting with expected label text

const MUTED_NOTIFICATION_SETTINGS_LABELS = {
  [XSettingKey.MUTED_DONT_FOLLOW]: 'You don’t follow',
  [XSettingKey.MUTED_DONT_FOLLOW_YOU]: 'Who don’t follow you',
  [XSettingKey.MUTED_NEW_ACCOUNT]: 'With a new account',
  [XSettingKey.MUTED_DEFAULT_PROFILE]: 'Who have a default profile photo',
  [XSettingKey.MUTED_UNCONFIRMED_EMAIL]: 'Who haven’t confirmed their email',
  [XSettingKey.MUTED_UNCONFIRMED_PHONE]:
    'Who haven’t confirmed their phone number',
}

// Setup gather stage definitions

enum StageKey {
  GATHER_SETTING_MUTED_DONT_FOLLOW = 'GATHER_SETTING_MUTED_DONT_FOLLOW',
  GATHER_SETTING_MUTED_DONT_FOLLOW_YOU = 'GATHER_SETTING_MUTED_DONT_FOLLOW_YOU',
  GATHER_SETTING_MUTED_NEW_ACCOUNT = 'GATHER_SETTING_MUTED_NEW_ACCOUNT',
  GATHER_SETTING_MUTED_DEFAULT_PROFILE_PHOTO = 'GATHER_SETTING_MUTED_DEFAULT_PROFILE_PHOTO',
  GATHER_SETTING_MUTED_UNCONFIRMED_EMAIL = 'GATHER_SETTING_MUTED_UNCONFIRMED_EMAIL',
  GATHER_SETTING_MUTED_UNCONFIRMED_PHONE = 'GATHER_SETTING_MUTED_UNCONFIRMED_PHONE',
  UPDATE_SETTING_MUTED_DONT_FOLLOW = 'UPDATE_SETTING_MUTED_DONT_FOLLOW',
  UPDATE_SETTING_MUTED_DONT_FOLLOW_YOU = 'UPDATE_SETTING_MUTED_DONT_FOLLOW_YOU',
  UPDATE_SETTING_MUTED_NEW_ACCOUNT = 'UPDATE_SETTING_MUTED_NEW_ACCOUNT',
  UPDATE_SETTING_MUTED_DEFAULT_PROFILE_PHOTO = 'UPDATE_SETTING_MUTED_DEFAULT_PROFILE_PHOTO',
  UPDATE_SETTING_MUTED_UNCONFIRMED_EMAIL = 'UPDATE_SETTING_MUTED_UNCONFIRMED_EMAIL',
  UPDATE_SETTING_MUTED_UNCONFIRMED_PHONE = 'UPDATE_SETTING_MUTED_UNCONFIRMED_PHONE',
  UNDO_SETTING_MUTED_DONT_FOLLOW = 'UNDO_SETTING_MUTED_DONT_FOLLOW',
  UNDO_SETTING_MUTED_DONT_FOLLOW_YOU = 'UNDO_SETTING_MUTED_DONT_FOLLOW_YOU',
  UNDO_SETTING_MUTED_NEW_ACCOUNT = 'UNDO_SETTING_MUTED_NEW_ACCOUNT',
  UNDO_SETTING_MUTED_DEFAULT_PROFILE_PHOTO = 'UNDO_SETTING_MUTED_DEFAULT_PROFILE_PHOTO',
  UNDO_SETTING_MUTED_UNCONFIRMED_EMAIL = 'UNDO_SETTING_MUTED_UNCONFIRMED_EMAIL',
  UNDO_SETTING_MUTED_UNCONFIRMED_PHONE = 'UNDO_SETTING_MUTED_UNCONFIRMED_PHONE',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_MUTED_DONT_FOLLOW,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForCheckbox(
        userContext.updateStoredAutomationErrors,
        userContext.updateStoredInitialValues,
        MUTED_NOTIFICATION_SETTINGS_LABELS,
        XSettingKey.MUTED_DONT_FOLLOW,
        TwitterRecommendationKey.TWITTER_MUTED_NOTIFICATIONS,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_MUTED_DONT_FOLLOW_YOU,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForCheckbox(
        userContext.updateStoredAutomationErrors,
        userContext.updateStoredInitialValues,
        MUTED_NOTIFICATION_SETTINGS_LABELS,
        XSettingKey.MUTED_DONT_FOLLOW_YOU,
        TwitterRecommendationKey.TWITTER_MUTED_NOTIFICATIONS,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_MUTED_NEW_ACCOUNT,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForCheckbox(
        userContext.updateStoredAutomationErrors,
        userContext.updateStoredInitialValues,
        MUTED_NOTIFICATION_SETTINGS_LABELS,
        XSettingKey.MUTED_NEW_ACCOUNT,
        TwitterRecommendationKey.TWITTER_MUTED_NOTIFICATIONS,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_MUTED_DEFAULT_PROFILE_PHOTO,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForCheckbox(
        userContext.updateStoredAutomationErrors,
        userContext.updateStoredInitialValues,
        MUTED_NOTIFICATION_SETTINGS_LABELS,
        XSettingKey.MUTED_DEFAULT_PROFILE,
        TwitterRecommendationKey.TWITTER_MUTED_NOTIFICATIONS,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_MUTED_UNCONFIRMED_EMAIL,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForCheckbox(
        userContext.updateStoredAutomationErrors,
        userContext.updateStoredInitialValues,
        MUTED_NOTIFICATION_SETTINGS_LABELS,
        XSettingKey.MUTED_UNCONFIRMED_EMAIL,
        TwitterRecommendationKey.TWITTER_MUTED_NOTIFICATIONS,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_MUTED_UNCONFIRMED_PHONE,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForCheckbox(
        userContext.updateStoredAutomationErrors,
        userContext.updateStoredInitialValues,
        MUTED_NOTIFICATION_SETTINGS_LABELS,
        XSettingKey.MUTED_UNCONFIRMED_PHONE,
        TwitterRecommendationKey.TWITTER_MUTED_NOTIFICATIONS,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_MUTED_DONT_FOLLOW,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.MUTED_DONT_FOLLOW]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_DONT_FOLLOW],
      )
      if (currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_DONT_FOLLOW],
      )
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_MUTED_DONT_FOLLOW_YOU,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.MUTED_DONT_FOLLOW_YOU]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_DONT_FOLLOW_YOU],
      )
      if (currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_DONT_FOLLOW_YOU],
      )
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_MUTED_NEW_ACCOUNT,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.MUTED_NEW_ACCOUNT]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_NEW_ACCOUNT],
      )
      if (currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_NEW_ACCOUNT],
      )
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_MUTED_DEFAULT_PROFILE_PHOTO,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.MUTED_DEFAULT_PROFILE]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_DEFAULT_PROFILE],
      )
      if (currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_DEFAULT_PROFILE],
      )
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_MUTED_UNCONFIRMED_EMAIL,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.MUTED_UNCONFIRMED_EMAIL]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_UNCONFIRMED_EMAIL],
      )
      if (currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_UNCONFIRMED_EMAIL],
      )
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_MUTED_UNCONFIRMED_PHONE,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.MUTED_UNCONFIRMED_PHONE]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_UNCONFIRMED_PHONE],
      )
      if (currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_UNCONFIRMED_PHONE],
      )
      return null
    },
  },
]

// Execution code

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_MUTED_DONT_FOLLOW,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.MUTED_DONT_FOLLOW]
      ) {
        return null
      }

      const prevValue = initialValues[XSettingKey.MUTED_DONT_FOLLOW]
      const currentValue = await getCurrentValueForCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_DONT_FOLLOW],
      )
      if (prevValue === currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_DONT_FOLLOW],
      )
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_MUTED_DONT_FOLLOW_YOU,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.MUTED_DONT_FOLLOW_YOU]
      ) {
        return null
      }

      const prevValue = initialValues[XSettingKey.MUTED_DONT_FOLLOW_YOU]
      const currentValue = await getCurrentValueForCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_DONT_FOLLOW_YOU],
      )
      if (prevValue === currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_DONT_FOLLOW_YOU],
      )
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_MUTED_NEW_ACCOUNT,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.MUTED_NEW_ACCOUNT]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_NEW_ACCOUNT],
      )
      const prevValue = initialValues[XSettingKey.MUTED_NEW_ACCOUNT]
      if (prevValue === currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_NEW_ACCOUNT],
      )
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_MUTED_DEFAULT_PROFILE_PHOTO,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.MUTED_DEFAULT_PROFILE]
      ) {
        return null
      }

      const prevValue = initialValues[XSettingKey.MUTED_DEFAULT_PROFILE]
      const currentValue = await getCurrentValueForCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_DEFAULT_PROFILE],
      )
      if (prevValue === currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_DEFAULT_PROFILE],
      )
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_MUTED_UNCONFIRMED_EMAIL,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.MUTED_UNCONFIRMED_EMAIL]
      ) {
        return null
      }

      const prevValue = initialValues[XSettingKey.MUTED_UNCONFIRMED_EMAIL]
      const currentValue = await getCurrentValueForCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_UNCONFIRMED_EMAIL],
      )
      if (prevValue === currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_UNCONFIRMED_EMAIL],
      )
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_MUTED_UNCONFIRMED_PHONE,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: mutedNotificationsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.MUTED_UNCONFIRMED_PHONE]
      ) {
        return null
      }

      const prevValue = initialValues[XSettingKey.MUTED_UNCONFIRMED_PHONE]
      const currentValue = await getCurrentValueForCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_UNCONFIRMED_PHONE],
      )
      if (prevValue === currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        MUTED_NOTIFICATION_SETTINGS_LABELS[XSettingKey.MUTED_UNCONFIRMED_PHONE],
      )
      return null
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
